import React from 'react';

import FormControlLabel from '@material-ui/core/FormControlLabel';
import Paper from '@material-ui/core/Paper';
import { makeStyles } from '@material-ui/core/styles';
import Switch from '@material-ui/core/Switch';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';

import { ExpandableTableHead } from './expandable-table-head';
import { ExpandableTableRow } from './expandable-table-row';

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    marginTop: theme.spacing(1),
  },
  paper: {
    width: '100%',
  },
  table: {
    minWidth: 750,
  },
  tableWrapper: {
    overflowX: 'auto',
  },
}));

export function ExpandableTable(props) {
  const { children, columns, pagination = false, rows } = props;

  const classes = useStyles();
  const expandable = !!children;

  const [dense, setDense] = React.useState(true);
  const [order, setOrder] = React.useState('asc');
  const [orderBy, setOrderBy] = React.useState('calories');
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  function handleRequestSort(event, name) {
    const descending = orderBy === name && order === 'desc';
    setOrder(descending ? 'asc' : 'desc');
    setOrderBy(name);
  }

  function handleChangePage(event, newPage) {
    setPage(newPage);
  }

  function handleChangeRowsPerPage(event) {
    setRowsPerPage(+event.target.value);
  }

  function handleChangeDense(event) {
    setDense(event.target.checked);
  }

  function renderEmptySpace() {
    if (!pagination) {
      return null;
    }

    const emptyRows = rowsPerPage - Math.min(rowsPerPage, rows.length - page * rowsPerPage);

    if (emptyRows <= 0 || !children) {
      return null;
    }

    return (
      <TableRow style={{ height: 49 * emptyRows }}>
        <TableCell colSpan={12} />
      </TableRow>
    );
  }

  function* renderHeaderColumns() {
    if (expandable) {
      yield <TableCell key="arrow" />;
    }

    for (const { align, field: name, size, sortable = true, title, type } of columns) {
      yield (
        <ExpandableTableHead
          align={align}
          expandable={expandable}
          key={name}
          name={name}
          onRequestSort={handleRequestSort}
          order={order}
          orderBy={orderBy}
          size={size}
          sortable={sortable}
          title={title}
          type={type}
        />
      );
    }
  }

  function * renderRows() {
    const sortedRows = stableSort(rows, createComparison(order, orderBy));
    const pageRows = pagination
      ? sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      : sortedRows;

    if (pageRows.length <= 0) {
      yield (
        <TableRow key="key-no-records">
          <TableCell colSpan={12}>
            <div style={{ padding: '1rem', textAlign: 'center' }}>
              No records to display
            </div>
          </TableCell>
        </TableRow>
      );
    }

    for (const row of pageRows) {
      yield (
        <ExpandableTableRow
          columns={columns}
          expandable={expandable}
          key={row.id}
          row={row}
        >
          {expandable ? children(row) : null}
        </ExpandableTableRow>
      );
    }
  }

  return (
    <div className={classes.root}>
      <Paper className={classes.paper}>
        <div className={classes.tableWrapper}>

          <Table className={classes.table} size={dense ? 'small' : 'medium'}>

            <TableHead>
              <TableRow>
                {Array.from(renderHeaderColumns())}
              </TableRow>
            </TableHead>

            <TableBody>
              {Array.from(renderRows())}
              {renderEmptySpace()}
            </TableBody>

          </Table>
        </div>

        {children && pagination &&
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={rows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
          />
        }
      </Paper>

      <FormControlLabel
        control={<Switch checked={dense} onChange={handleChangeDense}/>}
        label="Dense"
        style={{ display: 'none' }}
      />

    </div>
  );
}

function createComparison(order, orderBy) {
  return order === 'desc'
    ? (a, b) => compare(a, b, orderBy)
    : (a, b) => -compare(a, b, orderBy);

  function compare(a, b, orderBy) {
    if (b.fields[orderBy] < a.fields[orderBy]) {
      return -1;
    }

    if (b.fields[orderBy] > a.fields[orderBy]) {
      return 1;
    }

    return 0;
  }
}

function stableSort(rows, compare) {
  rows = rows ?? [];

  // box
  const stabilized = rows.map((row, index) => ({ row, index }));

  stabilized.sort((a, b) => {
    const order = compare(a.row, b.row);

    if (order !== 0) {
      return order;
    }

    return a.index - b.index;
  });

  // unbox
  return stabilized.map(({ row }) => row);
}
